import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
} from "@mui/material";

import { useAtom } from "jotai";
import { isAdminAtom, themeAtom } from "../../../../-Atoms";
import { db } from "../../../../firebaseConfig";
import { doc, updateDoc } from "firebase/firestore";

const CameraSettings = ({
  jobData,
  clientData,
  setHasUnsavedChanges,
}) => {
  const [isAdmin] = useAtom(isAdminAtom);
  const [theme] = useAtom(themeAtom);

  const [clientRetouching, setClientRetouching] = useState("ITS Retouching");
  const [jobRetouching, setJobRetouching] = useState("ITS Retouching");
  const [backgroundType, setBackgroundType] = useState("");

  const [cameraModelLife, setCameraModelLife] = useState("");
  const [cameraModelProduct, setCameraModelProduct] = useState("");

  const [cameraRatioLife, setCameraRatioLife] = useState("");
  const [cameraRatioProduct, setCameraRatioProduct] = useState("");

  const [cropInCaptureLife, setCropInCaptureLife] = useState("");
  const [cropInCaptureProduct, setCropInCaptureProduct] = useState("");

  const [supplyRatioLife, setSupplyRatioLife] = useState("");
  const [supplyRatioProduct, setSupplyRatioProduct] = useState("");

  const isLife = jobData?.jobTypeFull?.includes("-L");
  const isProduct = jobData?.jobTypeFull?.includes("-P");

  useEffect(() => {
    if (clientData) {
      const clientRetouchingValue = clientData.noRetouching === true ? "No Retouching" : "ITS Retouching";
      setClientRetouching(clientRetouchingValue);
      
      // Set job retouching to match client retouching by default, or use job-specific value if it exists
      const jobRetouchingValue = jobData?.noRetouching !== undefined 
        ? (jobData.noRetouching === true ? "No Retouching" : "ITS Retouching")
        : clientRetouchingValue;
      setJobRetouching(jobRetouchingValue);

      setBackgroundType(
        clientData.backgroundType
          ? clientData.backgroundType
          : clientData.isDigitalBackground
          ? "Digital Background"
          : "Organic Background"
      );

      setCameraModelLife(clientData.cameraModelLife || "");
      setCameraModelProduct(clientData.cameraModelProduct || "");

      setCameraRatioLife(clientData.cameraRatioLife || "");
      setCameraRatioProduct(clientData.cameraRatioProduct || "");

      setCropInCaptureLife(clientData.cropInCaptureLife || "");
      setCropInCaptureProduct(clientData.cropInCaptureProduct || "");

      setSupplyRatioLife(clientData.supplyRatioLife || "");
      setSupplyRatioProduct(clientData.supplyRatioProduct || "");
    }
  }, [clientData, jobData]);

  const updateFirestore = (field, value, variant) => {
    if (!clientData || !clientData.id) return;

    let updateField = field;
    let updateValue = value;

    if (variant) {
      updateField = `${field}${variant}`;
    }

    if (field === "backgroundType") {
      updateField = "isDigitalBackground";
      updateValue = value === "Digital Background";
    }

    if (field === "clientRetouching") {
      updateField = "noRetouching";
      updateValue = value === "No Retouching";
    }

    updateDoc(doc(db, "ClientDatabase", clientData.id), { [updateField]: updateValue })
      .then(() => {
        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      })
      .catch((e) => console.error(e));
  };

  const updateJobFirestore = (field, value) => {
    if (!jobData || !jobData.id) return;

    let updateField = field;
    let updateValue = value;

    if (field === "jobRetouching") {
      updateField = "noRetouching";
      updateValue = value === "No Retouching";
    }

    updateDoc(doc(db, "JobData", jobData.id), { [updateField]: updateValue })
      .then(() => {
        if (setHasUnsavedChanges) {
          setHasUnsavedChanges(false);
        }
      })
      .catch((e) => console.error(e));
  };

  const handleFieldFocus = () => {
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  const handleFieldBlur = (field, value, variant) => {
    if (!isAdmin) {
      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(false);
      }
      return;
    }
    updateFirestore(field, value, variant);
  };

  const handleSelectChange = (setter, field, variant) => (event) => {
    const value = event.target.value;
    setter(value);
    if (!isAdmin) {
      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(false);
      }
      return;
    }
    updateFirestore(field, value, variant);
  };

  const handleJobSelectChange = (setter, field) => (event) => {
    const value = event.target.value;
    setter(value);
    if (!isAdmin) {
      if (setHasUnsavedChanges) {
        setHasUnsavedChanges(false);
      }
      return;
    }
    updateJobFirestore(field, value);
  };


  const handleTextFieldChange = (setter, field, variant) => (event) => {
    const value = event.target.value;
    setter(value);
    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  return (
    <FormControl
      fullWidth
      sx={{
        position: "relative",
        "& .MuiInputLabel-root": {
          color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.7)",
          fontWeight: 500,
          fontSize: "0.95rem",
          transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
        },
      }}
      variant="filled"
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          borderRadius: "10px",
          overflow: "hidden",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 2,
        }}
      >
        <FormControl
          variant="filled"
          size="small"
          sx={{ width: "100%" }}
          disabled={!isAdmin}
        >
          <InputLabel id="job-retouching-label">Job Retouching</InputLabel>
          <Select
            labelId="job-retouching-label"
            id="job-retouching"
            value={jobRetouching}
            onChange={handleJobSelectChange(setJobRetouching, "jobRetouching")}
            onFocus={handleFieldFocus}
            disableUnderline
            sx={{
              borderRadius: "8px",
              backgroundColor:
                theme === "dark"
                  ? "rgba(45,45,45,0.7)"
                  : "rgba(250,250,250,0.9)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                  : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(55,55,55,0.8)"
                    : "rgba(255,255,255,1)",
                boxShadow:
                  theme === "dark"
                    ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                    : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
              },
              "&.Mui-focused": {
                boxShadow:
                  theme === "dark"
                    ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                    : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
              },
            }}
          >
            <MenuItem value="ITS Retouching">ITS Retouching</MenuItem>
            <MenuItem value="No Retouching">No Retouching</MenuItem>
          </Select>
        </FormControl>

        <FormControl
          variant="filled"
          size="small"
          sx={{ width: "100%" }}
          disabled={!isAdmin}
        >
          <InputLabel id="client-retouching-label">Client Retouching</InputLabel>
          <Select
            labelId="client-retouching-label"
            id="client-retouching"
            value={clientRetouching}
            onChange={handleSelectChange(setClientRetouching, "clientRetouching")}
            onFocus={handleFieldFocus}
            disableUnderline
            sx={{
              borderRadius: "8px",
              backgroundColor:
                theme === "dark"
                  ? "rgba(45,45,45,0.7)"
                  : "rgba(250,250,250,0.9)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                  : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(55,55,55,0.8)"
                    : "rgba(255,255,255,1)",
                boxShadow:
                  theme === "dark"
                    ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                    : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
              },
              "&.Mui-focused": {
                boxShadow:
                  theme === "dark"
                    ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                    : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
              },
            }}
          >
            <MenuItem value="ITS Retouching">ITS Retouching</MenuItem>
            <MenuItem value="No Retouching">No Retouching</MenuItem>
          </Select>
        </FormControl>

        <FormControl
          variant="filled"
          size="small"
          sx={{ width: "100%" }}
          disabled={!isAdmin}
        >
          <InputLabel id="background-type-label">Background Type</InputLabel>
          <Select
            labelId="background-type-label"
            id="background-type"
            value={backgroundType}
            onChange={handleSelectChange(setBackgroundType, "backgroundType")}
            onFocus={handleFieldFocus}
            disableUnderline
            sx={{
              borderRadius: "8px",
              backgroundColor:
                theme === "dark"
                  ? "rgba(45,45,45,0.7)"
                  : "rgba(250,250,250,0.9)",
              boxShadow:
                theme === "dark"
                  ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                  : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
              "&:hover": {
                backgroundColor:
                  theme === "dark"
                    ? "rgba(55,55,55,0.8)"
                    : "rgba(255,255,255,1)",
                boxShadow:
                  theme === "dark"
                    ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                    : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
              },
              "&.Mui-focused": {
                boxShadow:
                  theme === "dark"
                    ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                    : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
              },
            }}
          >
            <MenuItem value="Digital Background">Digital Background</MenuItem>
            <MenuItem value="Plated Background">Plated Background</MenuItem>
            <MenuItem value="Organic Background">Organic Background</MenuItem>
          </Select>
        </FormControl>

        <Grid container spacing={2}>
          {isLife && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ width: "100%" }}
                  disabled={!isAdmin}
                >
                  <InputLabel id="camera-model-life-label">
                    Camera Model
                  </InputLabel>
                  <Select
                    labelId="camera-model-life-label"
                    id="camera-model-life"
                    value={cameraModelLife}
                    onChange={handleSelectChange(
                      setCameraModelLife,
                      "cameraModel",
                      "Life"
                    )}
                    onFocus={handleFieldFocus}
                    disableUnderline
                    sx={{
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                      "&.Mui-focused": {
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                            : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                      },
                    }}
                  >
                    <MenuItem value="R3">R3</MenuItem>
                    <MenuItem value="R5">R5</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ width: "100%" }}
                  disabled={!isAdmin}
                >
                  <InputLabel id="camera-ratio-life-label">
                    Camera Ratio
                  </InputLabel>
                  <Select
                    labelId="camera-ratio-life-label"
                    id="camera-ratio-life"
                    value={cameraRatioLife}
                    onChange={handleSelectChange(
                      setCameraRatioLife,
                      "cameraRatio",
                      "Life"
                    )}
                    onFocus={handleFieldFocus}
                    disableUnderline
                    sx={{
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                      "&.Mui-focused": {
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                            : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                      },
                    }}
                  >
                    <MenuItem value="FULL">FULL</MenuItem>
                    <MenuItem value="3x4">3x4</MenuItem>
                    <MenuItem value="1x1">1x1</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  variant="filled"
                  size="small"
                  id="supply-ratio-life"
                  label="Supply Ratio"
                  disabled={!isAdmin}
                  value={supplyRatioLife}
                  onChange={handleTextFieldChange(
                    setSupplyRatioLife,
                    "supplyRatio",
                    "Life"
                  )}
                  onFocus={handleFieldFocus}
                  onBlur={() =>
                    handleFieldBlur("supplyRatio", supplyRatioLife, "Life")
                  }
                  sx={{
                    width: "100%",
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                      "&.Mui-focused": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(60,60,60,0.9)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                            : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                      },
                    },
                  }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ width: "100%" }}
                  disabled={!isAdmin}
                >
                  <InputLabel id="crop-in-capture-life-label">
                    Crop In Capture
                  </InputLabel>
                  <Select
                    labelId="crop-in-capture-life-label"
                    id="crop-in-capture-life"
                    value={cropInCaptureLife}
                    onChange={handleSelectChange(
                      setCropInCaptureLife,
                      "cropInCapture",
                      "Life"
                    )}
                    onFocus={handleFieldFocus}
                    disableUnderline
                    sx={{
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                      "&.Mui-focused": {
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                            : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                      },
                    }}
                  >
                    <MenuItem value="Camera Ratio">Camera Ratio</MenuItem>
                    <MenuItem value="Supply Ratio">Supply Ratio</MenuItem>
                    <MenuItem value="NO CROP">NO CROP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {isProduct && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ width: "100%" }}
                  disabled={!isAdmin}
                >
                  <InputLabel id="camera-model-product-label">
                    Camera Model
                  </InputLabel>
                  <Select
                    labelId="camera-model-product-label"
                    id="camera-model-product"
                    value={cameraModelProduct}
                    onChange={handleSelectChange(
                      setCameraModelProduct,
                      "cameraModel",
                      "Product"
                    )}
                    onFocus={handleFieldFocus}
                    disableUnderline
                    sx={{
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                      "&.Mui-focused": {
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                            : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                      },
                    }}
                  >
                    <MenuItem value="R3">R3</MenuItem>
                    <MenuItem value="R5">R5</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ width: "100%" }}
                  disabled={!isAdmin}
                >
                  <InputLabel id="camera-ratio-product-label">
                    Camera Ratio
                  </InputLabel>
                  <Select
                    labelId="camera-ratio-product-label"
                    id="camera-ratio-product"
                    value={cameraRatioProduct}
                    onChange={handleSelectChange(
                      setCameraRatioProduct,
                      "cameraRatio",
                      "Product"
                    )}
                    onFocus={handleFieldFocus}
                    disableUnderline
                    sx={{
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                      "&.Mui-focused": {
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                            : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                      },
                    }}
                  >
                    <MenuItem value="FULL">FULL</MenuItem>
                    <MenuItem value="3x4">3x4</MenuItem>
                    <MenuItem value="1x1">1x1</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  variant="filled"
                  size="small"
                  id="supply-ratio-product"
                  label="Supply Ratio"
                  disabled={!isAdmin}
                  value={supplyRatioProduct}
                  onChange={handleTextFieldChange(
                    setSupplyRatioProduct,
                    "supplyRatio",
                    "Product"
                  )}
                  onFocus={handleFieldFocus}
                  onBlur={() =>
                    handleFieldBlur(
                      "supplyRatio",
                      supplyRatioProduct,
                      "Product"
                    )
                  }
                  sx={{
                    width: "100%",
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                      "&.Mui-focused": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(60,60,60,0.9)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                            : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                      },
                    },
                  }}
                  InputProps={{
                    disableUnderline: true,
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl
                  variant="filled"
                  size="small"
                  sx={{ width: "100%" }}
                  disabled={!isAdmin}
                >
                  <InputLabel id="crop-in-capture-product-label">
                    Crop In Capture
                  </InputLabel>
                  <Select
                    labelId="crop-in-capture-product-label"
                    id="crop-in-capture-product"
                    value={cropInCaptureProduct}
                    onChange={handleSelectChange(
                      setCropInCaptureProduct,
                      "cropInCapture",
                      "Product"
                    )}
                    onFocus={handleFieldFocus}
                    disableUnderline
                    sx={{
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      boxShadow:
                        theme === "dark"
                          ? "0 2px 5px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 2px 5px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 12px rgba(0,0,0,0.25), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.3)"
                            : "0 4px 12px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.3)",
                      },
                      "&.Mui-focused": {
                        boxShadow:
                          theme === "dark"
                            ? "0 4px 15px rgba(0,0,0,0.3), 0 0 0 1px rgba(255,255,255,0.1), 0 0 0 3px rgba(100,100,255,0.5)"
                            : "0 4px 15px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05), 0 0 0 3px rgba(100,100,255,0.5)",
                      },
                    }}
                  >
                    <MenuItem value="Camera Ratio">Camera Ratio</MenuItem>
                    <MenuItem value="Supply Ratio">Supply Ratio</MenuItem>
                    <MenuItem value="NO CROP">NO CROP</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </FormControl>
  );
};

export default CameraSettings;
